import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Loader2, Save, Upload } from 'lucide-react';
import { checkAuth, clearToken, saveContent, uploadImage } from '../../admin/api';
import defaultContent from '../../content/defaultContent';
import { deepMerge } from '../../content/ContentContext';
import BrandMark from '../../components/BrandMark';
import AdminHeader from './AdminHeader';

export default function BrandAdmin() {
  const nav = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    (async () => {
      const ok = await checkAuth();
      if (!ok) { nav('/admin/login'); return; }
      try {
        const r = await fetch('/api/content', { cache: 'no-store' });
        const data = r.ok ? await r.json() : {};
        setContent(deepMerge(defaultContent, data || {}));
      } catch {
        setContent(deepMerge(defaultContent, {}));
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const flash = (message) => { setToast(message); setTimeout(() => setToast(''), 2600); };
  const site = content?.site || {};

  const setSite = (patch) => {
    setContent((prev) => ({ ...prev, site: { ...(prev.site || {}), ...patch } }));
  };

  const uploadAsset = async (file, key) => {
    if (!file) return;
    setSaving(true);
    try {
      const { url } = await uploadImage(file);
      setSite({ [key]: `${url}?v=${Date.now()}` });
      flash(key === 'logoUrl' ? 'Logo 已上传，保存后生效' : '标签图标已上传，保存后生效');
    } catch (e) {
      flash(e.message || '上传失败');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveContent(content);
      flash('品牌设置已保存');
    } catch (e) {
      flash(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return <div className="flex min-h-screen items-center justify-center text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 加载中...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="品牌设置" viewHref="/" viewLabel="看网站" onLogout={() => { clearToken(); nav('/admin/login'); }} />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">品牌 Logo</h1>
              <p className="mt-1 text-sm text-gray-500">上传后会用于后台左上角、官网导航 Logo；标签图标用于浏览器标签页小图标。</p>
            </div>
            <button onClick={save} disabled={saving} className="btn btn-blue inline-flex items-center gap-2 disabled:opacity-60"><Save className="h-4 w-4" />保存设置</button>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 text-sm font-bold text-slate-800">后台 / 官网 Logo</div>
              <div className="mb-4 flex h-24 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
                {site.logoUrl ? <img src={site.logoUrl} alt="Logo 预览" className="max-h-16 max-w-[220px] object-contain" /> : <BrandMark size="lg" />}
              </div>
              <label className="btn btn-white inline-flex cursor-pointer items-center gap-2">
                <Upload className="h-4 w-4" />上传 Logo
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/x-icon" className="hidden" onChange={(e) => uploadAsset(e.target.files?.[0], 'logoUrl')} />
              </label>
              <p className="mt-2 text-xs text-gray-500">建议使用透明背景 PNG 或 SVG，方形或横版都可以。</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 text-sm font-bold text-slate-800">浏览器标签图标</div>
              <div className="mb-4 flex h-24 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
                {site.faviconUrl ? <img src={site.faviconUrl} alt="标签图标预览" className="h-12 w-12 object-contain" /> : <ImageIcon className="h-10 w-10 text-gray-300" />}
              </div>
              <label className="btn btn-white inline-flex cursor-pointer items-center gap-2">
                <Upload className="h-4 w-4" />上传标签图标
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/x-icon" className="hidden" onChange={(e) => uploadAsset(e.target.files?.[0], 'faviconUrl')} />
              </label>
              <p className="mt-2 text-xs text-gray-500">建议 64x64 或 128x128 PNG；不上传时会自动使用 Logo。</p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">品牌名称
              <input className="form-input" value={site.brandName || ''} onChange={(e) => setSite({ brandName: e.target.value })} />
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">浏览器标题
              <input className="form-input" value={site.company || ''} onChange={(e) => setSite({ company: e.target.value })} />
            </label>
          </div>
        </section>
      </main>
      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">{toast}</div>}
    </div>
  );
}
